import ApiService from "./ApiService";
import { getAdminContentLanguage } from "./LangPrefs";

const StatsGridService = {
    /**
     * Get reviewed puzzle statistics by genre from API
     * @param {string} puzzleType - The type of puzzle (e.g., 'HIDDEN_MESSAGE', 'WORD_SEARCH', 'CROSSWORD')
     * @returns {Promise<Array>} Array containing genre statistics with reviewed puzzle counts
     */
    getReviewedStatsByGenre: async (puzzleType = 'HIDDEN_MESSAGE') => {
        try {
            const params = puzzleType ? { puzzleType } : {};
            const language = getAdminContentLanguage();
            if (language && language !== "english") {
                params.language = language;
            }
            const result = await ApiService.getData(`/v1/stats/grid/reviewed/by-genre`, params);

            // Transform the API response to match the expected format
            return result.map(item => ({
                genre: item.genre,
                reviewedCount: item.count,
                puzzleType: puzzleType
            }));
        } catch (error) {
            console.error(`Error fetching reviewed stats by genre for ${puzzleType}:`, error);
            throw error;
        }
    },

    /**
     * Get puzzle play statistics
     * @param {string} puzzleId - The puzzle ID
     * @param {string} puzzleType - The type of puzzle (e.g., 'HIDDEN_MESSAGE', 'WORD_SEARCH', 'CROSSWORD')
     * @returns {Promise<Object>} Object containing total, completed, and successRate
     */
    getPuzzleStats: async (puzzleId, puzzleType = 'HIDDEN_MESSAGE') => {
        try {
            const params = puzzleType ? { puzzleType } : {};
            const language = getAdminContentLanguage();
            if (language && language !== "english") {
                params.language = language;
            }
            // Use /v1/stats/cross/ endpoint for CROSSWORD puzzles, otherwise use /v1/stats/grid/
            const endpoint = puzzleType === 'CROSSWORD' 
                ? `/v1/stats/cross/${puzzleId}/play`
                : `/v1/stats/grid/${puzzleId}/play`;
            const result = await ApiService.getData(endpoint, params);
            return result;
        } catch (error) {
            console.error(`Error fetching puzzle stats for ID ${puzzleId}:`, error);
            throw error;
        }
    },

    /**
     * Get average total score across all puzzles
     * @param {string} puzzleType - The type of puzzle (e.g., 'HIDDEN_MESSAGE', 'WORD_SEARCH', 'CROSSWORD')
     * @returns {Promise<Object>} Object containing average score value
     */
    getAverageTotalScore: async (puzzleType = 'HIDDEN_MESSAGE') => {
        try {
            const params = puzzleType ? { puzzleType } : {};
            const language = getAdminContentLanguage();
            if (language && language !== "english") {
                params.language = language;
            }
            const result = await ApiService.getData(`/v1/stats/grid/average-total-score`, params);
            return result;
        } catch (error) {
            console.error("Error fetching average total score:", error);
            throw error;
        }
    },

    /**
     * Get play statistics for a user
     * @param {string} userId - The user ID
     * @param {string} puzzleType - The type of puzzle (e.g., 'HIDDEN_MESSAGE', 'WORD_SEARCH', 'CROSSWORD')
     * @returns {Promise<Object>} Object containing play statistics
     */
    getPlayStatistics: async (userId, puzzleType = 'hw') => {
        try {
            const params = puzzleType ? { puzzleType } : {};
            const language = getAdminContentLanguage();
            if (language && language !== "english") {
                params.language = language;
            }
            const response = await ApiService.getData(`/v1/stats/grid/${userId}/plays`, params);
            console.log("Play statistics response:", response);

            // Handle different possible response structures
            if (response && typeof response === 'object') {
                // Check if response has an error code
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to fetch play statistics");
                }

                // Return the response directly or unwrap if nested
                return response.data || response.content || response;
            }

            return response;
        } catch (error) {
            console.error("Error fetching play statistics:", error);
            throw error;
        }
    },

    /**
     * Get comprehensive dashboard statistics
     * @param {string} puzzleType - The puzzle type (WORD_SEARCH, HIDDEN_MESSAGE, CROSSWORD)
     * @returns {Promise<Object>} Complete dashboard statistics
     */
    getDashboardStats: async (puzzleType = "HIDDEN_MESSAGE") => {
        try {
            const language = getAdminContentLanguage();
            const overviewParams = {};
            if (language && language !== "english") {
                overviewParams.language = language;
            }
            const [stateGenreStats, stateDifficultyStats, overviewStats] = await Promise.all([
                StatsGridService.getStateGenreStats(puzzleType),
                StatsGridService.getStateDifficultyStats(puzzleType),
                ApiService.getData(`/v1/stats/grid/overview`, overviewParams)
            ]);

            return {
                genres: stateGenreStats,
                difficultyStats: stateDifficultyStats,
                overview: overviewStats
            };
        } catch (error) {
            console.error("Error fetching dashboard stats:", error);
            throw error;
        }
    },

    /**
     * Get puzzle statistics by state and genre from API
     * @param {string} puzzleType - The puzzle type (WORD_SEARCH, HIDDEN_MESSAGE, CROSSWORD)
     * @returns {Promise<Object>} Object containing genre statistics organized by status
     */
    getStateGenreStats: async (puzzleType = "HIDDEN_MESSAGE") => {
        try {
            const params = puzzleType ? { puzzleType } : {};
            const language = getAdminContentLanguage();
            if (language && language !== "english") {
                params.language = language;
            }
            const result = await ApiService.getData(`/v1/stats/grid/state-genre`, params);

            // Transform the array format into a more usable object structure
            const genreStats = {};

            result.forEach(([status, genre, count]) => {
                if (!genreStats[genre]) {
                    genreStats[genre] = {
                        name: genre,
                        stats: {
                            DRAFT: 0,
                            READY_TO_REVIEW: 0,
                            REVIEWED: 0,
                            PUBLISHED: 0,
                            DISCARDED: 0
                        }
                    };
                }
                genreStats[genre].stats[status] = count;
            });

            // Convert to array format for easier iteration
            return Object.values(genreStats);
        } catch (error) {
            console.error("Error fetching state-genre stats:", error);
            throw error;
        }
    },

    /**
     * Get puzzle statistics by state, genre, and difficulty from API
     * @param {string} puzzleType - The puzzle type (WORD_SEARCH, HIDDEN_MESSAGE, CROSSWORD)
     * @returns {Promise<Object>} Object containing genre difficulty statistics organized by status
     */
    getStateDifficultyStats: async (puzzleType = "HIDDEN_MESSAGE") => {
        try {
            const params = puzzleType ? { puzzleType } : {};
            const language = getAdminContentLanguage();
            if (language && language !== "english") {
                params.language = language;
            }
            const result = await ApiService.getData(`/v1/stats/grid/state-difficulty`, params);

            // Handle null or undefined result
            if (!result || !Array.isArray(result)) {
                console.warn("Invalid result from state-difficulty API:", result);
                return [];
            }

            // Transform the array format into a more usable object structure
            const genreDifficultyStats = {};

            result.forEach((item) => {
                // Handle invalid array items
                if (!Array.isArray(item) || item.length < 4) {
                    console.warn("Invalid item in state-difficulty result:", item);
                    return;
                }

                const [status, genre, difficulty, count] = item;

                // Skip if any required field is missing
                if (!status || !genre || !difficulty || count === undefined) {
                    console.warn("Missing required fields in state-difficulty item:", item);
                    return;
                }

                if (!genreDifficultyStats[genre]) {
                    genreDifficultyStats[genre] = {
                        name: genre,
                        difficultyStats: {
                            DRAFT: { EASY: 0, MEDIUM: 0, HARD: 0 },
                            READY_TO_REVIEW: { EASY: 0, MEDIUM: 0, HARD: 0 },
                            REVIEWED: { EASY: 0, MEDIUM: 0, HARD: 0 },
                            PUBLISHED: { EASY: 0, MEDIUM: 0, HARD: 0 },
                            DISCARDED: { EASY: 0, MEDIUM: 0, HARD: 0 }
                        }
                    };
                }
                // Ensure the status exists in difficultyStats
                if (!genreDifficultyStats[genre].difficultyStats[status]) {
                    genreDifficultyStats[genre].difficultyStats[status] = { EASY: 0, MEDIUM: 0, HARD: 0 };
                }
                // Ensure the difficulty exists in the status object
                if (genreDifficultyStats[genre].difficultyStats[status][difficulty] !== undefined) {
                    genreDifficultyStats[genre].difficultyStats[status][difficulty] = count;
                } else if (difficulty !== "PRO" && difficulty !== "ELITE" && difficulty !== "INSANE") {
                    console.warn(`Unknown difficulty level: ${difficulty} for status: ${status}, genre: ${genre}`);
                }
            });

            // Convert to array format for easier iteration
            return Object.values(genreDifficultyStats);
        } catch (error) {
            console.error("Error fetching state-difficulty stats:", error);
            throw error;
        }
    },

    /**
     * Get homework overview statistics
     * @param {string} _puzzleType - The puzzle type ('hw' or 'cross')
     * @returns {Promise<Object>} Object containing overview statistics including published puzzles
     */
    getOverviewStats: async () => {
        try {
            const params = {};
            const language = getAdminContentLanguage();
            if (language && language !== "english") {
                params.language = language;
            }
            const result = await ApiService.getData(`/v1/stats/grid/overview`, params);
            return result;
        } catch (error) {
            console.error("Error fetching overview stats:", error);
            throw error;
        }
    },

    /**
     * Get puzzle count statistics by state
     * @param {string} state - The puzzle state (e.g., 'PUBLISHED', 'DRAFT', 'REVIEWED', etc.)
     * @param {string} puzzleType - The puzzle type (WORD_SEARCH, HIDDEN_MESSAGE, CROSSWORD)
     * @returns {Promise<Object>} Object containing countByDifficulty and countByType statistics
     */
    getStateCountStats: async (state, puzzleType = "HIDDEN_MESSAGE") => {
        try {
            const params = puzzleType ? { puzzleType } : {};
            const language = getAdminContentLanguage();
            if (language && language !== "english") {
                params.language = language;
            }
            const result = await ApiService.getData(`/v1/stats/grid/${state}/count`, params);
            return result;
        } catch (error) {
            console.error(`Error fetching ${state} count stats:`, error);
            throw error;
        }
    },

    /**
     * Get puzzle counts grouped by state
     * @param {string} puzzleType - The puzzle type (HIDDEN_MESSAGE, WORD_SEARCH, etc.) - optional
     * @param {string} language - The language (english, french, etc.)
     * @returns {Promise<Object>} Object with state keys, each containing countByDifficulty and countByType
     */
    countByStateGrouped: async (puzzleType = null, language = "english") => {
        try {
            const params = {};
            if (puzzleType) params.puzzleType = puzzleType;
            if (language) params.language = language;
            const result = await ApiService.getData(`/v1/stats/grid/group-by-state`, params);
            return result;
        } catch (error) {
            console.error("Error fetching count by state grouped:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/grid/tags?language=english&puzzleType=HIDDEN_MESSAGE
     * Per-tag REVIEWED vs PUBLISHED grid counts, play sessions, and last published time.
     */
    statsByTag: async (language = "english", puzzleType = null) => {
        try {
            const params = {};
            if (language) params.language = language;
            const type = typeof puzzleType === "string" ? puzzleType.trim().toUpperCase() : "";
            if (type === "HIDDEN_MESSAGE" || type === "WORD_SEARCH") {
                params.puzzleType = type;
            }
            return await ApiService.getData("/v1/stats/grid/tags", params);
        } catch (error) {
            console.error("Error fetching grid tag stats:", error);
            throw error;
        }
    }
};

export default StatsGridService;

